import cx from 'classnames';
import * as React from 'react';

import getPropClasses from 'lib/getPropClasses';

import './styles/Heading.scss';

export type HeadingLevelStyle = '1' | '2' | '3' | '4' | '5' | '6';
export type HeadingAlign = 'left' | 'center' | 'right';

// prettier-ignore
type Props = {
  align?: HeadingAlign,
  children?: React.ReactNode,
  className?: string,
  /** Remove `margin-bottom` */
  flush?: boolean,
  /** Use other level styling */
  levelStyle?: HeadingLevelStyle | null,
  divided?: boolean,
} & DefaultProps;

type DefaultProps = {
  /** Mapping to h1, h2, etc. HTML elements */
  level: HeadingLevelStyle;
};

/**
 * ## Maps to native [text heading elements](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Heading_Elements).
 *
 *
 * ```example
 * <Heading level="1">
 *   Heading level 1
 * </Heading>
 * ```
 *
 * ```example
 * <Heading level="2">
 *   Heading level 2
 * </Heading>
 * ```
 *
 * ```example
 * <Heading level="3">
 *   Heading level 3
 * </Heading>
 * ```
 *
 * ```example
 * <Heading level="4">
 *   Heading level 4
 * </Heading>
 * ```
 *
 * ```example
 * <Heading level="5">
 *   Heading level 5
 * </Heading>
 * ```
 *
 * ```example
 * <Heading level="6">
 *   Heading level 6
 * </Heading>
 * ```
 *
 * ```example
 * <Heading align="center" flush level="1">
 *   Heading level 1
 * </Heading>
 * ```
 */

class Heading extends React.Component<Props> {
  static propClasses = {
    align: {
      left: 'Heading--left',
      center: 'Heading--center',
      right: 'Heading--right',
    },
    flush: 'Heading--flush',
    levelStyle: {
      /* eslint-disable quote-props */
      '1': 'Heading--1',
      '2': 'Heading--2',
      '3': 'Heading--3',
      '4': 'Heading--4',
      '5': 'Heading--5',
      '6': 'Heading--6',
      /* eslint-enable quote-props */
    },
    divided: 'Heading--divided',
  };

  static defaultProps: DefaultProps = {
    level: '2',
  };

  render() {
    const { children, className, level, levelStyle } = this.props;
    let header = 'h2';

    if (level === '1') {
      header = 'h1';
    } else if (level === '3') {
      header = 'h3';
    } else if (level === '4') {
      header = 'h4';
    } else if (level === '5') {
      header = 'h5';
    } else if (level === '6') {
      header = 'h6';
    }

    // eslint-disable-next-line no-negated-condition
    if (!levelStyle) {
      return React.createElement(
        header,
        {
          className: cx(
            'Heading',
            // $FlowIssue
            `Heading--${level}`,
            getPropClasses(this.props, Heading.propClasses),
            className
          ),
        },
        children
      );
    } else {
      return React.createElement(
        header,
        {
          className: cx('Heading', getPropClasses(this.props, Heading.propClasses), className),
        },
        children
      );
    }
  }
}

export default Heading;
